//---------------------------------------------------------
// Imports
//---------------------------------------------------------
import {Stack, Typography} from '@mui/material';
import {NO_NAME} from '../lbRuleEditLogic';
import AccordionBox from 'components/element/AccordionBox';
import ParamBox from 'components/element/ParamBox';
import DropDownSelectBox from 'components/element/DropDownSelectBox';
import HorizontalStack from 'components/layout/HorizontalStack';
import {t} from 'i18next';
import React, {Fragment, useCallback} from 'react';
import {IServiceArguments} from 'types/load_balancer';

//---------------------------------------------------------
// Component
//---------------------------------------------------------
export default function BasicSettingsForm(props: {value: IServiceArguments; onChange: (val: Partial<IServiceArguments>) => void; params?: any; isEdit?: boolean; errors?: Record<string, string>}) {
	const {value, onChange, params, isEdit = false, errors = {}} = props;

	// Send a DELTA, not {...value, field}: a stale full-object spread from a
	// mount-time dropdown auto-default clobbers sibling sub-forms' fields
	// (see LBInputForm.handleServiceArguments).
	const handleChange = useCallback((field: keyof IServiceArguments) => (newValue: any) => onChange({[field]: newValue}), [onChange]);

	// Note: protocol defaults to 'tcp' upstream (LBInputForm formData init) and in
	// the dropdown's display value, so no setState-in-effect is needed here — that
	// pattern (with an onChange identity that changed every render) drove the
	// "Maximum update depth exceeded" render loop this form once had.

	// Validate port range
	const portRangeError = React.useMemo(() => {
		const portMin = value?.port;
		const portMax = value?.portMax;
		
		// Only validate if both ports are valid numbers
		if (portMin !== undefined && portMin !== null && portMax !== undefined && portMax !== null &&
			portMin > 0 && portMin <= 65535 && portMax > 0 && portMax <= 65535) {
			if (portMin > portMax) {
				return t('Port Min must be less than or equal to Port Max');
			}
		}
		return null;
	}, [value?.port, value?.portMax]);

	return (
		<Fragment>
	   <ParamBox label={t('Rule Name')} value={isEdit && !value?.name ? t(NO_NAME) : value?.name ?? ''} onChange={handleChange('name')} param_desc={isEdit ? {...params?.name, required: false} : params?.name} disabled={isEdit} error={!!errors.name} helperText={errors.name}/>

	   <AccordionBox title={t('Basic Settings(*)')}>
			   <Stack spacing={2}>
					   <HorizontalStack>
							   <DropDownSelectBox 
									label={t('Protocol')} 
									value={value?.protocol ?? 'tcp'} 
									onChange={handleChange('protocol')} 
									item_list={[
										{id: 1, name: 'TCP', send_value: 'tcp'},
										{id: 2, name: 'UDP', send_value: 'udp'},
										{id: 3, name: 'SCTP', send_value: 'sctp'}
									]}
									disabled={isEdit}
							   />
							   {errors.protocol && <Typography variant="caption" color="error" data-testid="lb-protocol-error">{errors.protocol}</Typography>}
							   {/* <ParamBox label={t('Host')} value={value?.host ?? ''} onChange={handleChange('host')} param_desc={params?.host} /> */}
					   </HorizontalStack>

					   <HorizontalStack>
							   <ParamBox label={t('External IP')} value={value?.externalIP ?? ''} onChange={handleChange('externalIP')} param_desc={{...params?.externalIP, type: isEdit ? 'string' : 'ipaddress'}} disabled={isEdit} error={!!errors.externalIP} helperText={errors.externalIP} />
							   {/* <ParamBox label={t('Private IP')} value={value?.privateIP ?? ''} onChange={handleChange('privateIP')} param_desc={{...params?.privateIP, type: 'ipaddress'}} /> */}
					   </HorizontalStack>

					   <HorizontalStack>
						   <ParamBox label={t('Port Min')} value={value?.port?.toString() ?? ''} onChange={handleChange('port')} param_desc={{...params?.port, type: isEdit ? 'string' : 'port'}} disabled={isEdit} error={!!(portRangeError || errors.port)} helperText={portRangeError || errors.port || undefined} />
						   <ParamBox label={t('Port Max')} value={value?.portMax?.toString() ?? ''} onChange={handleChange('portMax')} param_desc={{...params?.portMax, type: isEdit ? 'string' : 'port'}} disabled={isEdit} error={!!(portRangeError || errors.portMax)} helperText={portRangeError || errors.portMax || undefined} />
					   </HorizontalStack>
			   </Stack>
	   </AccordionBox>
		</Fragment>
	);
}
